﻿<template>
  <div class="intercom-player relative w-full h-full bg-black overflow-hidden" :class="containerClass">
    <!-- Video element (door camera) -->
    <video
      ref="videoElement"
      class="w-full h-full object-contain"
      autoplay
      playsinline
      :muted="videoMuted"
      @click="ensurePlaying"
      @playing="onPlaying"
      @error="onVideoError" />

    <!-- Loading overlay -->
    <div
      v-if="isLoading || error || (!connected && !connecting)"
      class="media-failure-overlay absolute inset-0 flex items-center justify-center bg-black/60 z-10">
      <div class="flex flex-col items-center gap-3 text-white max-h-full min-h-0">
        <template v-if="isLoading">
          <div class="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
          <span class="text-xs text-white/60">{{ tt('Connecting...', 'Povezovanje...') }}</span>
        </template>
        <template v-else-if="error">
          <div
            class="media-failure-box bg-red-500/10 p-4 rounded-xl border border-red-500/20 flex flex-col items-center max-h-full min-h-0 overflow-y-auto">
            <span class="media-failure-box__icon material-symbols-outlined text-2xl text-red-500 mb-2">warning</span>
            <p class="media-failure-box__sentence text-xs text-red-400 font-medium px-2 text-center mb-3">
              {{ error }}
            </p>
            <!-- The cause, under the sentence (card t_4d5b3263): the doorphone
                 player's own verdict names which failure this is. Same map as
                 the live view's (services/mediaPath.ts); the gathering census
                 and the timings stay in the console and the hub's log. -->
            <p
              v-if="mediaPathCause"
              data-testid="media-path-cause"
              class="media-failure-box__cause text-[10px] leading-snug text-red-300/80 px-2 text-center mb-3">
              {{ mediaPathCause }}
            </p>
            <button
              @click="connect"
              class="px-4 py-1.5 bg-red-500 hover:bg-red-400 text-white rounded-lg text-xs font-medium transition-colors">
              {{ tt('Retry', 'Poskusi znova') }}
            </button>
          </div>
        </template>
        <template v-else>
          <span class="material-symbols-outlined text-3xl opacity-40">videocam_off</span>
          <button @click="connect" class="px-3 py-1 bg-white/10 hover:bg-white/20 rounded text-xs transition-colors">
            {{ tt('Connect', 'Poveži') }}
          </button>
        </template>
      </div>
    </div>

    <!-- LIVE badge -->
    <div
      v-if="connected && isPlaying"
      class="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 bg-black/40 backdrop-blur-md rounded-md border border-white/10 pointer-events-none">
      <span class="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
      <span class="text-[10px] font-bold tracking-widest text-white/90">{{ tt('LIVE', 'V ŽIVO') }}</span>
    </div>

    <!-- Mic mute indicator -->
    <div
      v-if="connected && micMuted"
      class="absolute top-3 right-3 px-2 py-1 bg-red-600/80 backdrop-blur-md rounded-md border border-red-400/20 pointer-events-none">
      <span class="text-[10px] font-bold text-white">{{ tt('MIC OFF', 'MIKROFON IZKLOPLJEN') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { routedFetch } from '../services/apiRouter';
import { DEFAULT_STUN_ICE_SERVERS } from '../services/webrtcConfig';
import { gatherIce, mediaPathReasonKey, watchMediaPath, type MediaPathFailureReason } from '../services/mediaPath';
import { resolveCameraEndpoints } from '../composables/useCameraStreamSession';
import { tt } from '../utils/consoleText';

const { t } = useI18n();

// ─── Props ────────────────────────────────────────────────────────────────────

const props = withDefaults(
  defineProps<{
    /** Camera ID from DB — used to fetch go2rtc WHEP endpoint */
    cameraId: string | number;
    /** If provided, use this WHEP URL directly (e.g., from the answer response) */
    whepUrl?: string;
    /** Ice servers override */
    iceServers?: RTCIceServer[];
    autoConnect?: boolean;
    /** Mute the doorphone's video audio (local speaker) */
    videoMuted?: boolean;
    containerClass?: string;
  }>(),
  {
    autoConnect: true,
    videoMuted: false,
  },
);

const emit = defineEmits<{
  connected: [];
  disconnected: [];
  error: [message: string];
  playing: [];
}>();

// ─── State ────────────────────────────────────────────────────────────────────

const videoElement = ref<HTMLVideoElement>();
const isPlaying = ref(false);
const connecting = ref(false);
const connected = ref(false);
const error = ref<string | null>(null);
/** Why the last attempt ended without a media path (card `t_4d5b3263`). */
const mediaPathReason = ref<MediaPathFailureReason | null>(null);
const micMuted = ref(false);
const peerConnection = shallowRef<RTCPeerConnection | null>(null);
let localStream: MediaStream | null = null;

const isLoading = computed(() => connecting.value || (connected.value && !isPlaying.value));

/**
 * The cause, under the sentence (card `t_4d5b3263`) — the doorphone's own
 * verdict, through the same map the live view uses (`services/mediaPath.ts`),
 * so the two surfaces cannot name the same failure differently. Read through
 * `t()` at paint time, so it follows a locale switch.
 */
const mediaPathCause = computed(() => {
  const key = mediaPathReasonKey(mediaPathReason.value);
  return key ? t(key) : null;
});

// ─── Lifecycle ─────────────────────────────────────────────────────────────────

onMounted(() => {
  if (props.autoConnect) connect();
});

onUnmounted(() => {
  disconnect();
});

watch(
  () => props.cameraId,
  () => {
    if (connected.value) {
      disconnect();
      connect();
    }
  },
);

// ─── Connection ───────────────────────────────────────────────────────────────

async function connect(): Promise<boolean> {
  if (connecting.value || connected.value) return connected.value;
  connecting.value = true;
  error.value = null;
  mediaPathReason.value = null;

  try {
    // ── 1. Resolve WHEP URL and ICE servers ───────────────────────────────
    let whepUrl = props.whepUrl;
    let ice: RTCIceServer[] = props.iceServers || DEFAULT_STUN_ICE_SERVERS;

    if (!whepUrl) {
      // Retries cover the brief window after a hub restart when go2rtc has no
      // registered streams yet.
      const endpoints = await resolveCameraEndpoints(String(props.cameraId));
      whepUrl = endpoints.whepUrl;
      ice = endpoints.iceServers || ice;
      // Avoid mixed-content when backend returns http:// behind TLS termination
      if (typeof window !== 'undefined' && whepUrl.startsWith('http://') && window.location.protocol === 'https:') {
        try {
          const parsed = new URL(whepUrl);
          whepUrl =
            parsed.host === window.location.host
              ? `${parsed.pathname}${parsed.search}`
              : `https://${parsed.host}${parsed.pathname}${parsed.search}`;
        } catch {
          // keep original
        }
      }
    }

    // ── 2. Acquire microphone for two-way audio ───────────────────────────
    try {
      localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    } catch {
      console.warn('[IntercomPlayer] Microphone not available — audio only receive');
    }

    // ── 3. Create peer connection ─────────────────────────────────────────
    const pc = new RTCPeerConnection({ iceServers: ice, iceCandidatePoolSize: 10 });
    peerConnection.value = pc;

    // Video: receive-only from doorphone
    pc.addTransceiver('video', { direction: 'recvonly' });

    // Audio: two-way  — we send mic, receive doorphone
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        pc.addTransceiver(audioTrack, { direction: 'sendrecv', streams: [localStream] });
      } else {
        pc.addTransceiver('audio', { direction: 'recvonly' });
      }
    } else {
      pc.addTransceiver('audio', { direction: 'recvonly' });
    }

    // Route incoming video/audio to the <video> element
    pc.ontrack = (event) => {
      const stream = event.streams[0];
      if (stream && videoElement.value) {
        videoElement.value.srcObject = stream;
        // Ensure the DOM muted property matches the prop (attribute-only
        // binding can silently keep audio muted after a src change)
        videoElement.value.muted = props.videoMuted;
        videoElement.value.play().catch(() => {
          // Autoplay with audio may be blocked until a user gesture — the
          // stream starts once the user clicks anywhere in the view
        });
      }
    };

    pc.onconnectionstatechange = () => {
      switch (pc.connectionState) {
        case 'connected':
          connected.value = true;
          connecting.value = false;
          error.value = null;
          emit('connected');
          break;
        case 'disconnected':
        case 'closed':
          connected.value = false;
          emit('disconnected');
          break;
        case 'failed':
          connected.value = false;
          error.value = tt('Connection failed', 'Povezava ni uspela');
          emit('error', tt('Connection failed', 'Povezava ni uspela'));
          break;
      }
    };

    // ── 4. Create offer ───────────────────────────────────────────────────
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // The gathering window, and what it waited for (card `t_20b35b28`): no
    // trickle, so the offer carries whatever was gathered when the window
    // closed. A window that closes while gathering is still open can produce a
    // relay-less offer, which fails exactly like the forever-`checking` case.
    const gathering = await gatherIce(pc);

    if (!gathering.complete) {
      console.warn(
        '[IntercomPlayer] ICE gathering was still open when the window closed — this offer may carry no relay candidate',
        { cameraId: props.cameraId, gathering },
      );
    }

    if (gathering.candidates.total === 0) {
      // Nothing to offer: no pair can be checked, so nothing is posted.
      console.warn('[IntercomPlayer] ICE gathered no candidates at all — the offer cannot reach the doorphone', {
        cameraId: props.cameraId,
        gathering,
      });
      const message = t('views.cameras.mediaPathFailed');
      disconnect();
      error.value = message;
      mediaPathReason.value = 'no-candidates';
      emit('error', message);
      return false;
    }

    // ── 5. WHEP exchange ──────────────────────────────────────────────────
    // Route via routedFetch so cloud mode proxies the SDP exchange through
    // the WebRTC data channel (the hub's local WHEP endpoint is not directly
    // reachable from Electron in cloud mode).
    const whepRes = await routedFetch(whepUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp' },
      body: pc.localDescription?.sdp,
    });

    if (!whepRes.ok)
      throw new Error(tt(`WHEP request failed: ${whepRes.status}`, `Zahteva WHEP ni uspela: ${whepRes.status}`));

    const answerSdp = await whepRes.text();
    await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: answerSdp }));

    // The answer is in — bound the wait for a live media path (card
    // `t_20b35b28`). Without this the doorphone's own view spins in
    // "Connecting…" forever whenever ICE never completes.
    const outcome = await watchMediaPath(pc).outcome;
    if (outcome === 'closed') {
      return false;
    }

    if (outcome !== 'live') {
      const message = t('views.cameras.mediaPathFailed');
      const reason: MediaPathFailureReason = outcome === 'failed' ? 'failed' : 'budget';
      console.warn('[IntercomPlayer] No media path — the doorphone took the offer and ICE never completed', {
        cameraId: props.cameraId,
        outcome,
        reason,
        gathering,
      });
      disconnect();
      error.value = message;
      mediaPathReason.value = reason;
      emit('error', message);
      return false;
    }

    return true;
  } catch (__caught: unknown) {
    const err = __caught as Error;
    console.error('[IntercomPlayer] Connection failed:', err);
    error.value = err.message || tt('Connection failed', 'Povezava ni uspela');
    mediaPathReason.value = null;
    connecting.value = false;
    emit('error', error.value!);
    peerConnection.value?.close();
    peerConnection.value = null;
    localStream?.getTracks().forEach((t) => t.stop());
    localStream = null;
    return false;
  }
}

function disconnect(): void {
  peerConnection.value?.close();
  peerConnection.value = null;
  localStream?.getTracks().forEach((t) => t.stop());
  localStream = null;

  if (videoElement.value) {
    videoElement.value.pause();
    videoElement.value.srcObject = null;
  }

  connected.value = false;
  connecting.value = false;
  error.value = null;
  mediaPathReason.value = null;
  isPlaying.value = false;
  micMuted.value = false;
}

async function reconnect(): Promise<void> {
  disconnect();
  await new Promise((r) => {
    setTimeout(r, 500);
  });
  connect();
}

// ─── Mic Control ──────────────────────────────────────────────────────────────

function toggleMic(): void {
  if (!localStream) return;
  localStream.getAudioTracks().forEach((t) => {
    t.enabled = !t.enabled;
  });
  micMuted.value = !localStream.getAudioTracks()[0]?.enabled;
}

/**
 * Switch this player's audio off entirely — stops sending the local mic and
 * mutes doorphone audio playback. Used when the SIP RTP bridge takes over
 * the audio path (e.g. DoorBird, whose RTSP stream has no audio track).
 */
function setAudioMuted(muted: boolean): void {
  if (muted && localStream) {
    localStream.getAudioTracks().forEach((t) => t.stop());
    localStream = null;
  }
  if (videoElement.value) {
    videoElement.value.muted = muted;
  }
  micMuted.value = muted;
}

/**
 * Set the doorphone playback volume (0..1). Independent of mic mute —
 * driven by the call overlay's speaker slider.
 */
function setAudioVolume(volume: number): void {
  const clamped = Math.max(0, Math.min(1, volume));
  if (videoElement.value) {
    videoElement.value.volume = clamped;
  }
}

// ─── Event Handlers ───────────────────────────────────────────────────────────

function onPlaying() {
  isPlaying.value = true;
  emit('playing');
}

/** Autoplay with audio can be blocked until a user gesture — retry on click */
function ensurePlaying(): void {
  const video = videoElement.value;
  if (!video) return;
  video.muted = props.videoMuted;
  if (video.paused) {
    video.play().catch(() => undefined);
  }
}

function onVideoError(e: Event) {
  console.error('[IntercomPlayer] Video error:', e);
  error.value = tt('Playback Error', 'Napaka predvajanja');
  isPlaying.value = false;
}

// ─── Expose ───────────────────────────────────────────────────────────────────

defineExpose({
  connect,
  disconnect,
  reconnect,
  toggleMic,
  setAudioMuted,
  setAudioVolume,
  micMuted,
  connected,
  connecting,
  isPlaying,
});
</script>
